// @ts-nocheck
/**
 * Demo Account Settings form — redesigned to obd3-gui pattern (ScrollPage
 * + PageHeader + Card + shadcn Input/Label). SUPERADMIN-only. Dual-mode
 * via :id param — presence flips to Edit.
 *
 * V1 QUIRKS preserved verbatim:
 *  - All fields except `total_demo_live_agent_overall` are disabled inputs
 *    (grey read-only) — the "Channels note" alert explains that channel
 *    count is derived; the other numeric inputs are read-only decorations
 *    even in Add mode.
 *  - `credit_limit_each_time` uses `Number()` (float), all others use
 *    `parseInt(v, 10)`.
 *  - Validation: every FIELDS entry must be ≥ 0; also
 *    `total_demo_live_agent_per_company > total_demo_agent_per_company` fails.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Info, Loader2, Save, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { postApiCall, patchApiCall, getApiCall } from '@/service/httpCall';
import {
  API_DEMO_ACCOUNT_SETTINGS_CREATE,
  API_DEMO_ACCOUNT_SETTINGS_UPDATE,
} from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';

const FIELDS = [
  { key: 'credit_limit_each_time', label: 'Credit Limit Each Time', float: true },
  { key: 'did_per_demo_company', label: 'DID Per Demo Company' },
  { key: 'total_demo_agent_per_company', label: 'Total Demo Agent Per Company' },
  { key: 'total_demo_live_agent_per_company', label: 'Total Demo Live Agent Per Company' },
  {
    key: 'total_demo_live_agent_overall',
    label: 'Total Demo Live Agent Overall (End User)',
  },
  { key: 'tts_per_demo_company_per_day', label: 'TTS Per Demo Company Per Day' },
  { key: 'call_center_per_company', label: 'Call Center Per Company' },
  { key: 'team_lead_per_company', label: 'Team Lead Per Company' },
  { key: 'expiry_days_per_recharge', label: 'Expiry Days Per Recharge' },
  {
    key: 'number_of_times_demo_account_can_be_recharged',
    label: 'Number Of Times Demo Account Can Be Recharged',
  },
];

export default function AddDemoAccountSettings() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<any>({
    credit_limit_each_time: '',
    did_per_demo_company: '',
    total_demo_agent_per_company: '',
    total_demo_live_agent_per_company: '',
    total_demo_live_agent_overall: '',
    tts_per_demo_company_per_day: '',
    call_center_per_company: '',
    team_lead_per_company: '',
    expiry_days_per_recharge: '',
    number_of_times_demo_account_can_be_recharged: '',
  });
  const [buttonDisable, setButtonDisable] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (!user || user.role !== USER_ROLES.SUPERADMIN) {
      toast.error('Only Super Admin can access demo account settings');
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    getApiCall(API_DEMO_ACCOUNT_SETTINGS_UPDATE.replace(':id', id))
      .then((resp: any) => {
        if (!resp) return;
        setForm({
          credit_limit_each_time: resp.credit_limit_each_time ?? '',
          did_per_demo_company: resp.did_per_demo_company ?? '',
          total_demo_agent_per_company: resp.total_demo_agent_per_company ?? '',
          total_demo_live_agent_per_company: resp.total_demo_live_agent_per_company ?? '',
          total_demo_live_agent_overall: resp.total_demo_live_agent_overall ?? '',
          tts_per_demo_company_per_day: resp.tts_per_demo_company_per_day ?? '',
          call_center_per_company: resp.call_center_per_company ?? '',
          team_lead_per_company: resp.team_lead_per_company ?? '',
          expiry_days_per_recharge: resp.expiry_days_per_recharge ?? '',
          number_of_times_demo_account_can_be_recharged:
            resp.number_of_times_demo_account_can_be_recharged ?? '',
        });
      })
      .catch((err: any) => toast.error(String(err)));
  }, [id]);

  const handleChange = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
  };

  function validateForm() {
    for (const f of FIELDS) {
      const v = form[f.key];
      if (v === '' || v === null || v === undefined || Number(v) < 0) {
        toast.warning(`${f.label} is required and must be non-negative`);
        return false;
      }
    }
    if (
      Number(form.total_demo_live_agent_per_company) >
      Number(form.total_demo_agent_per_company)
    ) {
      toast.warning('Total Demo Live Agent cannot be greater than Total Demo Agent');
      return false;
    }
    return true;
  }

  function submitForm(e: any) {
    e.preventDefault();
    setButtonDisable(true);
    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    const params = {
      credit_limit_each_time: Number(form.credit_limit_each_time),
      did_per_demo_company: parseInt(form.did_per_demo_company, 10),
      total_demo_agent_per_company: parseInt(form.total_demo_agent_per_company, 10),
      total_demo_live_agent_per_company: parseInt(
        form.total_demo_live_agent_per_company,
        10,
      ),
      total_demo_live_agent_overall: parseInt(form.total_demo_live_agent_overall, 10),
      tts_per_demo_company_per_day: parseInt(form.tts_per_demo_company_per_day, 10),
      call_center_per_company: parseInt(form.call_center_per_company, 10),
      team_lead_per_company: parseInt(form.team_lead_per_company, 10),
      expiry_days_per_recharge: parseInt(form.expiry_days_per_recharge, 10),
      number_of_times_demo_account_can_be_recharged: parseInt(
        form.number_of_times_demo_account_can_be_recharged,
        10,
      ),
    };

    const call = isEdit
      ? patchApiCall(API_DEMO_ACCOUNT_SETTINGS_UPDATE.replace(':id', id), params)
      : postApiCall(API_DEMO_ACCOUNT_SETTINGS_CREATE, params);

    call
      .then(() => {
        toast.success(
          `Demo account setting ${isEdit ? 'updated' : 'created'} successfully`,
        );
        setTimeout(() => navigate(`/${ROUTES.DEMO_ACCOUNT_SETTINGS.LIST}`), 1000);
      })
      .catch((err: any) => {
        setButtonDisable(false);
        toast.warning(String(err));
      });
  }

  return (
    <ScrollPage>
      <PageHeader
        title={isEdit ? 'Edit Demo Account Setting' : 'Add Demo Account Setting'}
        description="Global limits applied to every demo company (SUPERADMIN only)."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.DEMO_ACCOUNT_SETTINGS.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={submitForm}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-primary" />
              Demo limits
            </CardTitle>
            <CardDescription>
              Only <span className="font-medium text-foreground">Total Demo Live Agent Overall</span> is editable — the rest are informational.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {FIELDS.map((f: any) => (
                <div key={f.key} className="space-y-2">
                  <Label htmlFor={f.key}>
                    {f.label} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id={f.key}
                    type="number"
                    step={f.float ? '0.01' : '1'}
                    min="0"
                    value={form[f.key]}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    placeholder={f.label}
                    disabled={f.key !== 'total_demo_live_agent_overall'}
                  />
                </div>
              ))}
            </div>

            <div
              className="flex items-start gap-2 rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-800 dark:text-sky-300"
              role="status"
            >
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <strong>Channels note:</strong> Channels are not configured here. A demo
                company's Channels value is derived automatically as{' '}
                <strong>Total Demo Live Agent × 2</strong> and is not editable.
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.DEMO_ACCOUNT_SETTINGS.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={buttonDisable}>
            {buttonDisable ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {buttonDisable ? (isEdit ? 'Updating…' : 'Adding…') : isEdit ? 'Save changes' : 'Add setting'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
}
